/**
 * Service-worker middleware registry.
 *
 * sw.js's `interceptFetch` has always been an ordered chain of handlers —
 * each returns a Response (matched, stop) or null (fall through) — but the
 * chain was a literal array, and the rules that made it work lived in
 * comments ("ahead of the alwaysFetch kill switch, by design") and in
 * per-handler origin checks. This module makes the chain explicit: plugins
 * register with a name, an `order`, and **bindings**; the registry sorts
 * them, asserts the ordering constraints they declare, decides which
 * cross-origin requests the SW claims, and runs the dispatch loop.
 *
 * Project: projects/docs/2026/07/14/sw-middleware-plugins.md (Phase 1).
 *
 * Plugin shape:
 *
 *   {
 *     name:     'webdav-mount',            // unique
 *     order:    20,                        // lower runs first
 *     bindings: [{ domain: 'self', path: '/acq' }],   // default: self, '/'
 *     claim:    true | false | (request, url) => boolean,
 *     before:   ['always-fetch'],          // ordering assertions, checked at seal()
 *     after:    [],
 *     handle:   async (ev, ctx) => Response | null | undefined,
 *   }
 *
 * A binding is `{ domain, path, except? }`. `domain` is `'self'` (the SW's own
 * origin — the default), `'*'` (any origin), or an anchored origin such as
 * `https://peer.acequia.live`. `path` is a prefix matched on segment
 * boundaries (`/acq` matches `/acq` and `/acq/x`, never `/acquire`);
 * `except` lists paths under it the binding leaves alone, matched the same way.
 *
 * Cross-origin is pass-through by default (service-worker-cross-origin-
 * fetch-interference, 2026-08-14): the fetch listener only calls
 * `respondWith` for another origin when a plugin **claims** the request. A
 * plugin claims a cross-origin request when one of its non-`self` bindings
 * matches AND its `claim` allows it (`true` by default; a predicate refines
 * by method or URL; `false` means "run for whatever the SW handles, but
 * never lift pass-through on my account" — the Range guard). Same-origin
 * requests are always the SW's.
 *
 * `overrides` (from `config.swPlugins`, keyed by plugin name) may set
 * `order`, `bindings`, or `disabled: true` declaratively. That is the only
 * registration source besides the built-ins in this phase.
 *
 * Pure module — no deps.js, no stores — so it is unit-tested under node
 * (test/sw-registry.test.mjs) and bundled into acequia.js for sw.js.
 */

export const SELF = 'self'
export const ANY = '*'

const DEFAULT_BINDING = Object.freeze({ domain: SELF, path: '/' })

export function normalizePath(p) {
    if (p === undefined || p === null || p === '') return '/'
    let s = String(p)
    if (!s.startsWith('/')) s = '/' + s
    if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1)
    return s
}

function normalizeDomain(d) {
    if (d === undefined || d === null || d === SELF) return SELF
    if (d === ANY) return ANY
    let url
    try {
        url = new URL(String(d))
    } catch {
        throw new Error(`swRegistry: binding domain must be 'self', '*', or an origin — got ${JSON.stringify(d)}`)
    }
    // Anchored: an origin, nothing more. A path or query would silently
    // never match, so refuse it here rather than at request time.
    if (url.origin === 'null' || url.pathname !== '/' || url.search || url.hash) {
        throw new Error(`swRegistry: binding domain must be a bare origin (scheme://host[:port]) — got ${JSON.stringify(d)}`)
    }
    return url.origin
}

/**
 * A binding may carry `except`: paths under its own that it does not match
 * (a root mount that leaves `/auth` to the server). Segment-boundary matched,
 * as `path` is; omitted when empty.
 */
export function normalizeBinding(b = {}) {
    const out = { domain: normalizeDomain(b.domain), path: normalizePath(b.path) }
    if (b.except !== undefined) {
        if (!Array.isArray(b.except)) throw new Error(`swRegistry: binding except must be an array of paths — got ${JSON.stringify(b.except)}`)
        const except = b.except.map(normalizePath)
        if (except.length) out.except = Object.freeze(except)
    }
    return Object.freeze(out)
}

/** Prefix match on segment boundaries. */
export function pathMatches(prefix, pathname) {
    if (prefix === '/') return true
    if (!pathname.startsWith(prefix)) return false
    return pathname.length === prefix.length || pathname[prefix.length] === '/'
}

/** Does a (normalized) binding match this URL, given the SW's own origin? */
export function bindingMatches(binding, url, origin) {
    if (binding.domain === SELF) {
        if (url.origin !== origin) return false
    } else if (binding.domain !== ANY) {
        if (url.origin !== binding.domain) return false
    }
    if (!pathMatches(binding.path, url.pathname)) return false
    return !binding.except?.some((e) => pathMatches(e, url.pathname))
}

function toURL(u) {
    return u instanceof URL ? u : new URL(typeof u === 'string' ? u : u.url)
}

/** A plugin's bindings right now — static array, or the dynamic function's answer. */
export function currentBindings(plugin) {
    if (typeof plugin.bindings !== 'function') return plugin.bindings
    let list
    try {
        list = plugin.bindings()
    } catch (e) {
        console.error(`swRegistry: ${plugin.name} bindings() threw:`, e)
        return []
    }
    if (!Array.isArray(list)) return []
    const out = []
    for (const b of list) {
        try {
            out.push(normalizeBinding(b))
        } catch (e) {
            console.error(`swRegistry: ${plugin.name} produced a bad binding:`, e.message)
        }
    }
    return out
}

function normalizePlugin(plugin, override) {
    if (!plugin || typeof plugin !== 'object') throw new Error('swRegistry: plugin must be an object')
    const { name } = plugin
    if (typeof name !== 'string' || !name) throw new Error('swRegistry: plugin.name is required')
    if (typeof plugin.handle !== 'function') throw new Error(`swRegistry: plugin ${name} needs a handle(ev, ctx) function`)

    const order = override?.order ?? plugin.order ?? 50
    if (typeof order !== 'number' || Number.isNaN(order)) throw new Error(`swRegistry: plugin ${name} order must be a number`)

    // Bindings are a frozen array, or a function returning the current
    // array (a mount table that changes at runtime). A config override is
    // always an array and replaces either form.
    const rawBindings = override?.bindings ?? plugin.bindings ?? [DEFAULT_BINDING]
    let bindings
    if (typeof rawBindings === 'function') {
        bindings = rawBindings
    } else {
        if (!Array.isArray(rawBindings) || rawBindings.length === 0) {
            throw new Error(`swRegistry: plugin ${name} bindings must be a non-empty array or a function`)
        }
        bindings = Object.freeze(rawBindings.map(normalizeBinding))
    }

    const claim = plugin.claim === undefined ? true : plugin.claim
    if (!(typeof claim === 'boolean' || typeof claim === 'function')) {
        throw new Error(`swRegistry: plugin ${name} claim must be boolean or function`)
    }

    const list = (v, field) => {
        if (v === undefined) return Object.freeze([])
        if (!Array.isArray(v) || v.some((n) => typeof n !== 'string')) {
            throw new Error(`swRegistry: plugin ${name} ${field} must be an array of plugin names`)
        }
        return Object.freeze([...v])
    }

    return Object.freeze({
        name,
        order,
        bindings,
        claim,
        before: list(plugin.before, 'before'),
        after: list(plugin.after, 'after'),
        handle: plugin.handle,
    })
}

export function createRegistry({ origin, overrides = {} } = {}) {
    if (typeof origin !== 'string' || !origin) throw new Error('swRegistry: createRegistry needs the SW origin')
    overrides = overrides && typeof overrides === 'object' ? overrides : {}

    const byName = new Map()
    let seq = 0
    let sorted = null // set by seal()

    const requireSealed = (what) => {
        if (!sorted) throw new Error(`swRegistry: ${what} before seal()`)
    }

    const registry = {
        origin,

        /** Register a plugin. Returns the normalized plugin, or null if config disabled it. */
        register(plugin) {
            if (sorted) throw new Error('swRegistry: register() after seal()')
            const override = overrides[plugin?.name]
            if (override?.disabled) return null
            const p = normalizePlugin(plugin, override)
            if (byName.has(p.name)) throw new Error(`swRegistry: duplicate plugin name ${p.name}`)
            byName.set(p.name, { ...p, seq: seq++ })
            return p
        },

        /** Sort, check every declared ordering constraint, freeze. */
        seal() {
            if (sorted) return registry
            const list = [...byName.values()].sort((a, b) => a.order - b.order || a.seq - b.seq)
            const index = new Map(list.map((p, i) => [p.name, i]))
            for (const p of list) {
                for (const other of p.before) {
                    if (!index.has(other)) throw new Error(`swRegistry: ${p.name} declares before ${other}, which is not registered`)
                    if (index.get(p.name) > index.get(other)) throw new Error(`swRegistry: ordering violated — ${p.name} (order ${p.order}) must run before ${other} (order ${byName.get(other).order})`)
                }
                for (const other of p.after) {
                    if (!index.has(other)) throw new Error(`swRegistry: ${p.name} declares after ${other}, which is not registered`)
                    if (index.get(p.name) < index.get(other)) throw new Error(`swRegistry: ordering violated — ${p.name} (order ${p.order}) must run after ${other} (order ${byName.get(other).order})`)
                }
            }
            sorted = Object.freeze(list.map(({ seq: _s, ...p }) => Object.freeze(p)))
            return registry
        },

        get sealed() {
            return !!sorted
        },

        /** The plugins in dispatch order. */
        get plugins() {
            requireSealed('plugins')
            return sorted
        },

        get(name) {
            return byName.get(name) ?? null
        },

        /** Throw unless plugin `a` runs before plugin `b`. */
        assertOrder(a, b) {
            requireSealed('assertOrder')
            const ia = sorted.findIndex((p) => p.name === a)
            const ib = sorted.findIndex((p) => p.name === b)
            if (ia < 0) throw new Error(`swRegistry: no plugin named ${a}`)
            if (ib < 0) throw new Error(`swRegistry: no plugin named ${b}`)
            if (ia > ib) throw new Error(`swRegistry: ordering violated — ${a} must run before ${b}`)
            return true
        },

        /** Does this plugin run for this URL? */
        matches(plugin, url) {
            url = toURL(url)
            return currentBindings(plugin).some((b) => bindingMatches(b, url, origin))
        },

        /**
         * Does the SW answer for this request at all? Same-origin: always.
         * Cross-origin: only when a plugin's non-self binding matches and
         * its claim allows it.
         */
        claims(request) {
            requireSealed('claims')
            const url = toURL(request)
            if (url.origin === origin) return true
            for (const p of sorted) {
                if (p.claim === false) continue
                const bound = currentBindings(p).some((b) => b.domain !== SELF && bindingMatches(b, url, origin))
                if (!bound) continue
                if (p.claim === true || p.claim(request, url)) return true
            }
            return false
        },

        /**
         * Run the chain: first plugin whose bindings match and whose handle
         * returns a Response wins. A throwing handler is reported and
         * skipped — the chain continues, as the old loop did.
         */
        async dispatch(ev, ctx, { onError } = {}) {
            requireSealed('dispatch')
            const url = toURL(ev.request)
            for (const p of sorted) {
                if (!registry.matches(p, url)) continue
                try {
                    const resp = await p.handle(ev, ctx)
                    if (resp) return { resp, plugin: p }
                } catch (err) {
                    if (onError) onError(err, p)
                }
            }
            return { resp: null, plugin: null }
        },

        /** Serializable view for SW state reports and tests. */
        describe() {
            const list = sorted ?? [...byName.values()].sort((a, b) => a.order - b.order || a.seq - b.seq)
            return list.map((p) => ({
                name: p.name,
                order: p.order,
                bindings: currentBindings(p).map((b) => ({ ...b, ...(b.except ? { except: [...b.except] } : {}) })),
                dynamic: typeof p.bindings === 'function',
                claim: typeof p.claim === 'function' ? 'predicate' : p.claim,
                before: [...p.before],
                after: [...p.after],
            }))
        },
    }

    return registry
}
